'use client';

import { useEffect, useRef, useState } from 'react';

import { type TurnstileWidgetProps } from './types';

import { getRouteView } from '@/modules/activityTracker/referers';
import { Stdv1Stat } from '@/modules/observability/constants';
import { getLogger } from '@/modules/observability/logging';
import { recordMetric } from '@/modules/observability/metrics';
import {
  TURNSTILE_SCRIPT_URL,
  WIDGET_CONTAINER_ID,
  TURNSTILE_DUMMY_TOKEN,
} from '@/modules/turnstile/constants';
import { getTurnstileAction } from '@/modules/turnstile/getTurnstileAction';

export function TurnstileWidget({
  siteKey,
  onSuccessAction,
  onErrorAction,
  size = 'normal',
}: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const isLoadingScript = useRef(false);
  const logger = getLogger();

  // Load Turnstile script
  useEffect(() => {
    if (window.turnstile) {
      setIsScriptLoaded(true);
      return;
    }

    if (isLoadingScript.current || isScriptLoaded) {
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    script.defer = true;

    // Our CSP uses 'strict-dynamic', so a script injected here is only allowed
    // if it carries the request nonce. Copy it from an existing nonced script
    // (the content attribute is hidden by the browser, but the `.nonce` IDL
    // property still exposes the value to same-origin scripts).
    const nonce =
      document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
    if (nonce) {
      script.nonce = nonce;
    }

    script.onload = () => {
      setIsScriptLoaded(true);
      isLoadingScript.current = false;
    };

    script.onerror = () => {
      onErrorAction(new Error('Failed to load Turnstile script'));
    };

    document.head.appendChild(script);
    isLoadingScript.current = true;

    return () => {
      // Cleanup script if component unmounts
      document.head.removeChild(script);
    };
  }, [onErrorAction, isScriptLoaded]);

  // Render widget when script is loaded
  useEffect(() => {
    if (
      !isScriptLoaded ||
      !window.turnstile ||
      !containerRef.current ||
      widgetIdRef.current
    ) {
      return;
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        action: getTurnstileAction(window.location.pathname),
        callback: (token: string, preClearanceObtained: boolean) => {
          recordMetric({
            type: 'increment',
            stat: Stdv1Stat['turnstile.challenge.success'],
            tags: [`route:${getRouteView(window.location.pathname)}`],
          });

          // Passing preClearanceObtained: true if Dummy token faced (for development only)
          if (
            process.env.NEXT_PUBLIC_DEPLOY_ENV === 'development' &&
            token === TURNSTILE_DUMMY_TOKEN
          ) {
            onSuccessAction(token, true);
          }

          onSuccessAction(token, preClearanceObtained);
        },
        'error-callback': (error: unknown) => {
          recordMetric({
            type: 'increment',
            stat: Stdv1Stat['turnstile.challenge.failed'],
            tags: [`route:${getRouteView(window.location.pathname)}`],
          });

          onErrorAction(error);
        },
        theme: 'light',
        size,
      });
    } catch (error) {
      // Emit challenge failed metric
      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['turnstile.challenge.failed'],
        tags: [`route:${getRouteView(window.location.pathname)}`],
      });

      onErrorAction(error);
    }

    return () => {
      // Cleanup widget
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          logger.warn('Failed to remove Turnstile widget:', { error });
        }
      }
    };
  }, [isScriptLoaded, siteKey]);

  return (
    <div>
      <div
        ref={containerRef}
        id={WIDGET_CONTAINER_ID}
        style={{ minHeight: !widgetIdRef.current ? '65px' : 'auto' }}
      />
    </div>
  );
}
